/*
  The project itself — its name, whether it is archived, and whether it still exists.

  THREE CAPABILITIES THAT WERE COMPLETE AND HAD NO BUTTON. `renameProject` and `archiveProject` have
  been commands with handlers, authorisation and tests since P3, and `apps/web/src` contained zero
  references to either — so a project was permanently named after the spreadsheet it was imported from
  (`app.ts` derives the name from the filename), and archiving, though reversible and enforced on
  every write, could not be reached in either direction. `DELETE /api/projects/:id` was worse still:
  the route, the owner check and the confirm-by-name have existed as long, and `api.ts` had no method
  for it at all, so the only way to remove a body of work naming real employees was `curl`.

  ONE SURFACE, THREE DIFFERENT WEIGHTS, and they are deliberately not levelled. Renaming is typing.
  Archiving is a command with an inverse, one click, undone by the button that replaces it. Deleting
  takes the project's name typed back — and the requirement is the SERVER'S, not this dialog's
  (`app.ts`'s delete route parses `confirmName` and `projects.ts` compares it), so this passes on a
  check rather than inventing one. That is the same shape `DataPanel` uses for
  `rebuildRosterFromSource`, which is the product's other irreversible act, and looking alike is the
  point: a reader should recognise the shape before they read the words.

  OWNER-ONLY THROUGHOUT, and absent rather than disabled for everybody else — `renameProject` and
  `archiveProject` are both in `OWNER_ONLY_COMMANDS` and deletion is `canAdminProject`. A disabled
  button still tells a reader about a power they do not have, which is the argument `AccountModal`
  already makes for its admin section.
*/
import { useState } from 'react';
import type { Command } from '@integrate/contracts';
import { api, ApiError } from '../api.js';
import { Modal } from '../ui/Modal.js';

export function ProjectSettings({
  projectId,
  name,
  archived,
  isOwner,
  onDispatch,
  onDeleted,
  onClose,
}: {
  projectId: string;
  name: string;
  archived: boolean;
  isOwner: boolean;
  onDispatch(command: Command): Promise<boolean>;
  /** The project is gone; the caller has to leave it rather than re-render an empty one. */
  onDeleted(): void;
  onClose(): void;
}): JSX.Element {
  const [draft, setDraft] = useState(name);
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = (command: Command): void => {
    if (busy) return;
    setBusy(true);
    setError('');
    void onDispatch(command).finally(() => setBusy(false));
  };

  const destroy = (): void => {
    if (busy) return;
    setBusy(true);
    setError('');
    void api
      .deleteProject(projectId, confirmName)
      .then(() => onDeleted())
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiError || cause instanceof Error
            ? cause.message
            : 'The project could not be deleted.',
        );
        setBusy(false);
      });
  };

  if (!isOwner) {
    return (
      <Modal testId="project-settings" title="Project" onClose={onClose}>
        <p className="muted small" data-testid="project-settings-readonly">
          Only the project owner can rename, archive or delete this project.
        </p>
      </Modal>
    );
  }

  return (
    <Modal testId="project-settings" title="Project" onClose={onClose}>
      <div className="field">
        <label htmlFor="project-name">Name</label>
        <input
          id="project-name"
          data-testid="project-name"
          value={draft}
          disabled={busy || archived}
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>
      <button
        data-testid="project-rename"
        disabled={busy || archived || draft.trim() === '' || draft === name}
        onClick={() => run({ type: 'renameProject', name: draft.trim() })}
      >
        Rename
      </button>

      <h5 className="asm-grid-h">{archived ? 'Archived' : 'Archive'}</h5>
      <p className="muted asm-help">
        {archived
          ? 'This project is read-only. Every change is refused until it is taken out of the archive; nothing has been lost.'
          : 'Makes the project read-only for everybody. Reversible, and nothing is deleted — use it for an engagement that has finished rather than one you want removed.'}
      </p>
      <button
        data-testid={archived ? 'project-unarchive' : 'project-archive'}
        disabled={busy}
        onClick={() => run({ type: 'archiveProject', archived: !archived })}
      >
        {archived ? 'Take out of the archive' : 'Archive this project'}
      </button>

      <h5 className="asm-grid-h">Delete</h5>
      <p className="muted asm-help">
        Removes the roster, every scenario, every decision and the change log. There is no undo and no
        restore point — archiving is the reversible one. It is offered because a body of work naming
        real employees and their salaries has to be removable on request.
      </p>
      <div className="field">
        <label htmlFor="project-delete-confirm">
          Type <strong>{name}</strong> to confirm
        </label>
        <input
          id="project-delete-confirm"
          data-testid="project-delete-confirm"
          value={confirmName}
          disabled={busy}
          onChange={(e) => setConfirmName(e.target.value)}
        />
      </div>
      {/*
        The comparison is drawn as well as enforced, and the server compares it again. Disabling the
        button on a mismatch is a courtesy — it stops a typo becoming a refusal somebody has to read —
        and `projects.ts` is what makes the requirement real for a caller that is not this dialog.
      */}
      <button
        className="danger"
        data-testid="project-delete"
        disabled={busy || confirmName !== name}
        onClick={destroy}
      >
        {busy ? 'Deleting…' : 'Delete this project'}
      </button>
      {error !== '' && (
        <p className="muted small" data-testid="project-settings-error">
          {error}
        </p>
      )}
    </Modal>
  );
}
